import { useMemo, useState } from "react";
import { renderTextWithLineBreaks } from "../../utils/DeleteBr";
import MathText from "../VueQuestion/MathText";
import { PastillesNiveaux } from "../CollerNotes/PastilleNiveau";
import { Search } from 'lucide-react';

// ─── Palette (identique au mockup) ───
const T = {
  navy: "#1E3A8A", blueMid: "#3B5FCC", blueLight: "#5B7FE6", blueAccent: "#4A90D9",
  textPrimary: "#1A2744", textSecondary: "#6B7A99", textMuted: "#9AA5BD",
  borderLight: "rgba(30,58,138,0.08)", borderMid: "rgba(30,58,138,0.12)",
  green: "#22C55E", greenBg: "rgba(34,197,94,0.07)", greenBorder: "rgba(34,197,94,0.15)",
  orange: "#F59E0B", orangeBg: "rgba(245,158,11,0.07)", orangeBorder: "rgba(245,158,11,0.15)",
  red: "#EF4444", redBg: "rgba(239,68,68,0.06)", redBorder: "rgba(239,68,68,0.12)",
};

// ─── Helpers statut ───
const getStatus = (m) => {
  if (m >= 5) return "acquis";
  if (m > 3) return "encours";
  if (m >= 1) return "fragile";
  return "neval";
};

const getConf = (s) => ({
  acquis:  { label: "Acquis",      color: T.green,  bg: T.greenBg,  border: T.greenBorder },
  encours: { label: "En cours",    color: T.orange, bg: T.orangeBg, border: T.orangeBorder },
  fragile: { label: "Non acquis",  color: T.red,    bg: T.redBg,    border: T.redBorder },
  neval:   { label: "Non évalué",  color: "#9CA3AF", bg: "rgba(156,163,175,0.07)", border: "rgba(156,163,175,0.15)" },
}[s]);

// ─── Grain texture SVG (identique mockup) ───
const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='150' height='150' filter='url(%23n)' opacity='.5'/%3E%3C/svg%3E\")";

// ─── CSS responsive (variables + media queries) ───
const RESPONSIVE_CSS = `
  .acq-root {
    --card-pad: 20px 22px; --card-r: 18px;
    --score-fs: 30px; --score-sub: 14px;
    --title-fs: 14px; --meta-fs: 11px;
    --grid-min: 330px; --grid-gap: 14px;
    --sum-pad: 22px 24px; --sum-title: 15px; --sum-label: 13px; --sum-item: 12px; --sum-gap: 20px; --sum-mb: 18px;
    --search-pad: 12px 16px; --search-fs: 12px;
    --pill-fs: 11px; --pill-pad: 4px 10px;
    --strength-fs: 12px; --classe-fs: 12px; --ecart-fs: 12px;
    --needle-h: 38px;
    --legend-fs: 10px; --legend-dot: 8px;
    --filter-icon: 10px; --filter-gap: 5px;
    --title-clamp: 3; --sum-clamp: 1;
    padding: var(--acq-pad, 20px 0);
  }

  /* ─ Mobile ─ */
  @media (max-width:639px) {
    .acq-root {
      --card-pad: 16px 18px; --card-r: 14px;
      --score-fs: 24px; --score-sub: 11px;
      --title-fs: 13px; --meta-fs: 10px;
      --grid-min: 280px; --grid-gap: 10px;
      --sum-pad: 16px 18px; --sum-title: 13px; --sum-label: 11px; --sum-item: 11px; --sum-gap: 12px; --sum-mb: 14px;
      --search-pad: 10px 14px; --search-fs: 11px;
      --pill-fs: 10px; --pill-pad: 3px 8px;
      --strength-fs: 11px; --needle-h: 32px;
      --title-clamp: 2; --sum-clamp: 2;
    }
    .acq-sum-cols { grid-template-columns: 1fr !important }
  }

  /* ─ xl: 1280-1699px — compact ─ */
  @media (min-width:1280px) and (max-width:1699px) {
    .acq-root {
      --card-pad: 14px 16px; --card-r: 14px;
      --score-fs: 22px; --score-sub: 11px;
      --title-fs: 12px; --meta-fs: 10px;
      --grid-min: 250px; --grid-gap: 10px;
      --sum-pad: 14px 16px; --sum-title: 13px; --sum-label: 11px; --sum-item: 10px; --sum-gap: 14px; --sum-mb: 12px;
      --search-pad: 8px 12px; --search-fs: 11px;
      --pill-fs: 10px; --pill-pad: 3px 7px;
      --strength-fs: 10px; --classe-fs: 10px; --ecart-fs: 10px;
      --needle-h: 30px;
      --legend-fs: 9px; --legend-dot: 7px;
      --filter-icon: 9px; --filter-gap: 4px;
      --title-clamp: 2; --sum-clamp: 2;
    }
    .acq-sum-cols { grid-template-columns: 1fr 1fr !important }
  }

  /* ─ 2xl: 1700-1919px — medium ─ */
  @media (min-width:1700px) and (max-width:1919px) {
    .acq-root {
      --card-pad: 16px 18px; --card-r: 16px;
      --score-fs: 26px; --score-sub: 12px;
      --title-fs: 13px; --meta-fs: 10px;
      --grid-min: 290px; --grid-gap: 12px;
      --sum-pad: 18px 20px; --sum-title: 14px; --sum-label: 12px; --sum-item: 11px; --sum-gap: 16px; --sum-mb: 14px;
      --search-pad: 10px 14px; --search-fs: 11px;
      --pill-fs: 10px; --pill-pad: 3px 9px;
      --strength-fs: 11px; --classe-fs: 11px; --ecart-fs: 11px;
      --needle-h: 34px;
      --legend-fs: 9px; --legend-dot: 7px;
      --filter-icon: 9px;
      --title-clamp: 2; --sum-clamp: 2;
    }
  }

  /* 3xl: 1920px+ → defaults (déjà définis) */

  /* ─ Card title truncation (responsive line-clamp) ─ */
  .card-title {
    display: -webkit-box; -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--title-clamp, 3);
    overflow: hidden; text-overflow: ellipsis;
    word-break: break-word;
  }

  /* ─ Summary item text (responsive: 1 line 3xl, 2 lines xl/2xl) ─ */
  .sum-item-text {
    display: -webkit-box; -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--sum-clamp, 1);
    overflow: hidden; text-overflow: ellipsis;
    word-break: break-word; line-height: 1.35;
  }

  /* ─ Hover tooltip ─ */
  .trunc-tip { position: relative; }
  .trunc-tip .trunc-full {
    display: none; position: absolute; z-index: 9999;
    bottom: calc(100% + 6px); left: 0;
    padding: 8px 12px; max-width: 340px; width: max-content;
    background: #1E293B; color: #F1F5F9; font-size: 12px; line-height: 1.45;
    font-weight: 500; border-radius: 10px;
    box-shadow: 0 12px 32px rgba(0,0,0,0.18);
    pointer-events: none; white-space: normal; word-break: break-word;
  }
  .trunc-tip .trunc-full::after {
    content: ''; position: absolute; top: 100%; left: 16px;
    border: 5px solid transparent; border-top-color: #1E293B;
  }
  .trunc-tip:hover .trunc-full { display: block; }

  .card{transition:all .3s cubic-bezier(.4,0,.2,1);cursor:pointer;position:relative;z-index:1}
  .card:hover{transform:translateY(-5px)!important;z-index:10!important;box-shadow:0 20px 56px rgba(30,58,138,0.14), 0 8px 20px rgba(30,58,138,0.08), 0 2px 6px rgba(30,58,138,0.05), inset 0 1px 0 rgba(255,255,255,0.95)!important}
`;

// ─── Score (monospace) ───
const Score = ({ score, max = 6, sz = "md" }) => {
  const c = getConf(getStatus(score));
  const fsVar = sz === "lg" ? "var(--score-fs)" : sz === "md" ? "var(--score-fs)" : "var(--meta-fs)";
  const subVar = sz === "lg" ? "var(--score-sub)" : sz === "md" ? "var(--score-sub)" : undefined;
  return (
    <span style={{ fontFamily: "monospace", fontWeight: 700, color: c.color, fontSize: sz === "sm" ? 14 : fsVar, lineHeight: 1 }}>
      {score.toFixed(1)}
      <span style={{ fontSize: subVar || "0.5em", color: T.textMuted, fontWeight: 500 }}>/{max}</span>
    </span>
  );
};

// ─── Pill ───
const Pill = ({ status, small }) => {
  const c = getConf(status);
  return (
    <span style={{
      padding: small ? "2px 8px" : "var(--pill-pad)", borderRadius: 20,
      fontSize: small ? 10 : "var(--pill-fs)", fontWeight: 700,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      whiteSpace: "nowrap"
    }}>
      {c.label}
    </span>
  );
};

// ─── Ecart ▲/▼ ───
const Ecart = ({ e, c }) => {
  if (c == null) return null;
  const d = Math.round((e - c) * 100) / 100;
  const p = d >= 0;
  return (
    <span style={{ fontFamily: "monospace", fontSize: "var(--ecart-fs)", fontWeight: 600, color: p ? T.green : T.red }}>
      {p ? "▲" : "▼"} {p ? "+" : ""}{d.toFixed(2)}
    </span>
  );
};

// ─── Needle (identique mockup, avec collision detection) ───
const Needle = ({ eleve, classe, max = 6 }) => {
  if (classe == null) return null;
  const eP = (eleve / max) * 100, cP = (classe / max) * 100;
  const conf = getConf(getStatus(eleve));
  const sc = (v) => 6 + (v / 100) * 88;
  const ePos = sc(eP), cPos = sc(cP);
  const above = eleve >= classe;
  const close = Math.abs(ePos - cPos) < 8;
  const eRight = ePos >= cPos;

  return (
    <div style={{ width: "100%", position: "relative", height: "var(--needle-h)" }}>
      {/* Barre de fond */}
      <div style={{ position: "absolute", top: 12, left: 0, right: 0, height: 4, borderRadius: 2, background: "rgba(148,163,184,0.1)" }} />
      {/* Bande entre les deux */}
      <div style={{
        position: "absolute", top: 12, height: 4, borderRadius: 2,
        left: `${Math.min(cPos, ePos)}%`,
        width: `${Math.max(Math.abs(ePos - cPos), 0.5)}%`,
        background: above
          ? "linear-gradient(90deg,rgba(30,58,138,0.12),rgba(30,58,138,0.25))"
          : "linear-gradient(90deg,rgba(239,68,68,0.1),rgba(239,68,68,0.2))",
        transition: "all 0.6s ease"
      }} />
      {/* Marqueur classe */}
      <div style={{
        position: "absolute", top: 7, left: `${cPos}%`, transform: "translateX(-50%)",
        width: 14, height: 14, borderRadius: "50%",
        border: "2.5px solid #94A3B8", background: "white",
        transition: "left 0.6s ease", zIndex: 2
      }} />
      {/* Marqueur élève */}
      <div style={{
        position: "absolute", top: 5, left: `${ePos}%`, transform: "translateX(-50%)",
        width: 18, height: 18, borderRadius: "50%",
        background: conf.color, boxShadow: `0 0 8px ${conf.color}44`,
        border: "2.5px solid white", transition: "left 0.6s ease", zIndex: 3
      }} />
      {/* Label classe */}
      <div style={{
        position: "absolute", top: 23, left: `${cPos}%`,
        transform: close && !eRight ? "translateX(-90%)" : close ? "translateX(-10%)" : "translateX(-50%)",
        display: "flex", alignItems: "center", gap: 3, transition: "left 0.6s ease"
      }}>
        <div style={{ width: 7, height: 7, borderRadius: "50%", border: "1.5px solid #94A3B8", background: "white" }} />
        <span style={{ fontSize: 9.5, color: "#94A3B8", fontFamily: "monospace", fontWeight: 500, whiteSpace: "nowrap" }}>
          Cl. {classe.toFixed(1)}
        </span>
      </div>
      {/* Label élève */}
      <div style={{
        position: "absolute", top: 23, left: `${ePos}%`,
        transform: close && eRight ? "translateX(-10%)" : close ? "translateX(-90%)" : "translateX(-50%)",
        display: "flex", alignItems: "center", gap: 3, transition: "left 0.6s ease"
      }}>

      </div>
    </div>
  );
};

// ─── StrengthTag (identique mockup) ───
const StrengthTag = ({ label, score, max = 6, type }) => {
  if (!label) return null;
  const isF = type === "force";
  const isTrunc = label.length > 30;
  return (
    <div className={isTrunc ? "trunc-tip" : ""} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "var(--strength-fs)", color: T.textSecondary, lineHeight: 1.3 }}>
      <span style={{ fontSize: 13, flexShrink: 0 }}>{isF ? "✅" : "🔴"}</span>
      <span style={{ fontWeight: 600, color: T.textPrimary, flexShrink: 0 }}>{isF ? "Point fort" : "Fragilité"} :</span>
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: isTrunc ? "help" : undefined }}><MathText>{label}</MathText></span>
      {score !== undefined && <Score score={score} max={max} sz="sm" />}
      {isTrunc && <div className="trunc-full"><MathText>{label}</MathText></div>}
    </div>
  );
};

// ─── Card ───
const CARD_STYLE = {
  background: "linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.86) 70%, rgba(235,243,255,0.78) 100%)",
  backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
  padding: "var(--card-pad)", borderRadius: "var(--card-r)",
  border: "1px solid rgba(255,255,255,0.65)",
  boxShadow: "0 12px 40px rgba(30,58,138,0.1), 0 4px 12px rgba(30,58,138,0.06), 0 1px 3px rgba(30,58,138,0.04), inset 0 1px 0 rgba(255,255,255,0.9)",
  position: "relative", overflow: "visible", cursor: "pointer",
  display: "flex", flexDirection: "column",
};

const AcquisitionCard = ({ item, viewLevel, index, loaded, niveauClasse }) => {
  const st = getStatus(item.moyenne);
  const conf = getConf(st);
  const best = item.best;
  const worst = item.worst;
  const bE = best ? best.moyenne : null;
  const wE = worst ? worst.moyenne : null;

  return (
    <div className="card" style={{
      ...CARD_STYLE,
      opacity: loaded ? 1 : 0,
      transform: loaded ? "translateY(0)" : "translateY(12px)",
      transition: `all 0.4s cubic-bezier(.4,0,.2,1) ${index * 35}ms`,
    }}>
      {/* Grain subtil */}
      <div style={{
        position: "absolute", inset: 0, opacity: 0.018, pointerEvents: "none",
        borderRadius: "var(--card-r)", overflow: "hidden",
        backgroundImage: GRAIN, mixBlendMode: "multiply",
        background: "linear-gradient(135deg, rgba(30,58,138,0.03), rgba(59,95,204,0.02))"
      }} />
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", flex: 1 }}>
        {/* Header : titre + pill */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8, gap: 10 }}>
          {/* « 6e » : attendus d'un autre niveau que la classe (2026-09-29). */}
          <PastillesNiveaux niveaux={item.niveaux} niveauClasse={niveauClasse} />
          <div className={item.titre?.length > 40 ? "trunc-tip" : ""} style={{ flex: 1, minWidth: 0 }}>
            <h3 className="card-title" style={{ margin: 0, fontSize: "var(--title-fs)", fontWeight: 700, lineHeight: 1.35, color: T.textPrimary }}>
              {renderTextWithLineBreaks(item.titre)}
            </h3>
            {item.titre?.length > 40 && (
              <div className="trunc-full"><MathText>{item.titre}</MathText></div>
            )}
          </div>
          <Pill status={st} small />
        </div>

        {/* Label thème (pour descripteur et savoir) */}
        {viewLevel !== 'theme' && item.theme && (
          <div className={item.theme.length > 35 ? "trunc-tip" : ""} style={{ fontSize: "var(--meta-fs)", color: T.textMuted, marginBottom: 6 }}>
            <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: item.theme.length > 35 ? "help" : undefined }}>
              {item.theme}
            </span>
            {item.theme.length > 35 && <div className="trunc-full">{item.theme}</div>}
          </div>
        )}

        {/* Score + Classe + Ecart */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 6 }}>
          <div style={{ fontSize: "var(--score-fs)", fontWeight: 800, fontFamily: "monospace", lineHeight: 1, color: conf.color }}>
            {item.moyenne.toFixed(1)}<span style={{ fontSize: "var(--score-sub)", fontWeight: 500, color: T.textMuted }}>/{6}</span>
          </div>
          {item.moyenneClasse != null && (
            <div style={{ fontSize: "var(--classe-fs)", color: T.textSecondary }}>
              Classe : {item.moyenneClasse.toFixed(2)} <Ecart e={item.moyenne} c={item.moyenneClasse} />
            </div>
          )}
        </div>

        {/* Info counts */}
        {viewLevel === 'theme' && (
          <div style={{ fontSize: "var(--meta-fs)", color: T.textMuted, marginBottom: 10 }}>
            📋 {item.descCount} desc. · 🎯 {item.savoirCount} savoir{item.savoirCount > 1 ? 's' : ''}
          </div>
        )}
        {viewLevel === 'descripteur' && (
          <div style={{ fontSize: "var(--meta-fs)", color: T.textMuted, marginBottom: 10 }}>
            🎯 {item.count} savoir{item.count > 1 ? 's' : ''} évalué{item.count > 1 ? 's' : ''}
          </div>
        )}
        {viewLevel === 'savoir' && (
          <div style={{ fontSize: "var(--meta-fs)", color: T.textMuted, marginBottom: 10 }}>
            📝 {item.count} éval{item.count > 1 ? 's' : ''}
            {item.derniereEvaluation && ` · 📅 ${new Date(item.derniereEvaluation).toLocaleDateString('fr-FR')}`}
          </div>
        )}

        {/* Force / Fragilité (thème et descripteur) */}
        {(viewLevel === 'theme' || viewLevel === 'descripteur') && (best || worst) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
            {best && bE !== wE && <StrengthTag type="force" label={best.titre} score={bE} />}
            {worst && bE !== wE && <StrengthTag type="fragile" label={worst.titre} score={wE} />}
            {best && bE === wE && (
              <div style={{ fontSize: "var(--meta-fs)", color: T.textMuted, fontStyle: "italic" }}>Niveaux homogènes</div>
            )}
          </div>
        )}

        {/* Message contextuel (vue savoir) */}
        {viewLevel === 'savoir' && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 10, fontSize: "var(--strength-fs)", fontWeight: 600, color: conf.color }}>
            <span style={{ fontSize: 13 }}>{st === "acquis" ? "✅" : st === "encours" ? "🟡" : st === "fragile" ? "🔴" : "⚪"}</span>
            {st === "acquis" ? "Maîtrisé" : st === "encours" ? "En progression" : st === "fragile" ? "À travailler en priorité" : "Non évalué"}
          </div>
        )}

        {/* Needle — toujours en bas de la card */}
        <div style={{ paddingTop: 10, marginTop: "auto", borderTop: "2px solid rgba(30,58,138,0.1)", position: "relative" }}>
          <div style={{ position: "absolute", top: -1, left: 0, right: 0, height: 1, background: "linear-gradient(90deg, transparent 0%, rgba(30,58,138,0.06) 20%, rgba(30,58,138,0.06) 80%, transparent 100%)" }} />
          <Needle eleve={item.moyenne} classe={item.moyenneClasse} max={6} />
        </div>
      </div>
    </div>
  );
};

// ─── Section Résumé ───
const SummarySection = ({ items, viewLevel, niveauClasse }) => {
  const forces = items.filter(i => getStatus(i.moyenne) === "acquis");
  const fragiles = items.filter(i => getStatus(i.moyenne) === "fragile");
  const enCours = items.filter(i => getStatus(i.moyenne) === "encours");

  const fLbl = viewLevel === 'theme' ? "Thèmes forts" : viewLevel === 'descripteur' ? "Notions fortes" : "Savoirs forts";
  const frLbl = viewLevel === 'theme' ? "Thèmes fragiles" : viewLevel === 'descripteur' ? "Notions fragiles" : "Savoirs fragiles";

  const cols = [
    { items: forces, label: fLbl, icon: "✅" },
    { items: fragiles, label: frLbl, icon: "🔴" },
    { items: enCours, label: "En cours", icon: "🟡" },
  ];

  return (
    <div style={{
      background: "linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.86) 70%, rgba(235,243,255,0.78) 100%)",
      backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      borderRadius: "var(--card-r)", padding: "var(--sum-pad)",
      border: "1px solid rgba(255,255,255,0.7)",
      boxShadow: "0 12px 40px rgba(30,58,138,0.09), 0 4px 12px rgba(30,58,138,0.05), inset 0 1px 0 rgba(255,255,255,0.9)",
      marginBottom: "var(--sum-mb)", position: "relative", overflow: "hidden"
    }}>
      {/* Grain */}
      <div style={{ position: "absolute", inset: 0, opacity: 0.015, pointerEvents: "none", borderRadius: "var(--card-r)", backgroundImage: GRAIN, mixBlendMode: "multiply" }} />

    

      <div className="acq-sum-cols" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "var(--sum-gap)" }}>
        {cols.map((col, ci) => (
          <div key={ci}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: "var(--sum-label)" }}>{col.icon}</span>
              <span style={{ fontSize: "var(--sum-label)", fontWeight: 700, color: T.textPrimary }}>{col.label} ({col.items.length})</span>
            </div>
            {col.items.length === 0 ? (
              <div style={{ fontSize: "var(--sum-item)", color: T.textMuted, fontStyle: "italic" }}>Aucun</div>
            ) : (
              col.items.slice(0, 4).map((d, i) => (
                <div key={i} className={d.titre?.length > 30 ? "trunc-tip" : ""} style={{ fontSize: "var(--sum-item)", color: T.textSecondary, marginBottom: 5, display: "flex", alignItems: "flex-start", gap: 6 }}>
                  <PastillesNiveaux niveaux={d.niveaux} niveauClasse={niveauClasse} />
                  <span className="sum-item-text" style={{ flex: 1, minWidth: 0, cursor: d.titre?.length > 30 ? "help" : undefined }}>
                    <MathText>{d.titre}</MathText>
                  </span>
                  <Score score={d.moyenne} max={6} sz="sm" />
                  {d.titre?.length > 30 && <div className="trunc-full"><MathText>{d.titre}</MathText></div>}
                </div>
              ))
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Filtres acquisition ───
const STATUS_FILTERS = [
  { key: "all",     label: "Tous",        icon: "📊" },
  { key: "acquis",  label: "Acquis",      icon: "✅", color: T.green,  bg: T.greenBg,  border: T.greenBorder },
  { key: "encours", label: "En cours",    icon: "🟡", color: T.orange, bg: T.orangeBg, border: T.orangeBorder },
  { key: "fragile", label: "Non acquis",  icon: "🔴", color: T.red,    bg: T.redBg,    border: T.redBorder },
];

// ─── Composant principal ───
const AcquisitionEleve = ({ savoirEtudiés, viewLevel = 'savoir', searchTerm, setSearchTerm, niveauClasse = '' }) => {
  const [loaded, setLoaded] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const validItems = useMemo(() => {
    setLoaded(false);
    setTimeout(() => setLoaded(true), 80);
    return savoirEtudiés?.filter(s => s.moyenne != null && s.moyenne !== 0) || [];
  }, [savoirEtudiés]);

  const displayItems = useMemo(() => {
    if (statusFilter === "all") return validItems;
    return validItems.filter(i => getStatus(i.moyenne) === statusFilter);
  }, [validItems, statusFilter]);

  return (
    <div className="acq-root" style={{ padding: "20px 0", minHeight: 200, position: "relative" }}>
      <style>{RESPONSIVE_CSS}</style>

      {/* Résumé */}
      {validItems.length > 0 && <SummarySection items={validItems} viewLevel={viewLevel} niveauClasse={niveauClasse} />}

      {/* Recherche & Filtres par acquisition — toujours visible */}
      <div style={{
        background: "linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.86) 70%, rgba(235,243,255,0.78) 100%)",
        backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        borderRadius: "var(--card-r)", padding: "var(--search-pad)",
        border: "1px solid rgba(255,255,255,0.7)",
        boxShadow: "0 8px 28px rgba(30,58,138,0.07), 0 2px 8px rgba(30,58,138,0.04), inset 0 1px 0 rgba(255,255,255,0.9)",
        marginBottom: "var(--sum-mb)", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"
      }}>
        {/* Input recherche */}
        <div style={{ position: "relative", flex: 1, minWidth: 140 }}>
          <Search size={14} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: T.textMuted }} />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%", paddingLeft: 28, paddingRight: 10, paddingTop: 6, paddingBottom: 6,
              fontSize: "var(--search-fs)", background: "#FAFBFF",
              border: `1.5px solid ${T.borderMid}`, borderRadius: 8, outline: "none",
              color: T.textPrimary, fontFamily: "inherit",
            }}
          />
        </div>

        {/* Pills filtre par statut */}
        <div style={{ display: "flex", gap: "var(--filter-gap)", flexWrap: "wrap" }}>
          {STATUS_FILTERS.map(f => {
            const active = statusFilter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                style={{
                  padding: "var(--pill-pad)", borderRadius: 20,
                  fontSize: "var(--pill-fs)", fontWeight: 600, cursor: "pointer",
                  transition: "all 0.2s", display: "flex", alignItems: "center", gap: 4,
                  background: active ? (f.bg || T.blueAccent) : "rgba(30,58,138,0.03)",
                  color: active ? (f.color || "white") : T.textMuted,
                  border: active ? `1.5px solid ${f.border || T.blueAccent}` : `1.5px solid ${T.borderLight}`,
                }}
              >
                <span style={{ fontSize: "var(--filter-icon)" }}>{f.icon}</span>
                <span>{f.label}</span>
                {f.key !== "all" && (
                  <span style={{ fontSize: "var(--filter-icon)", opacity: 0.7 }}>
                    ({validItems.filter(i => getStatus(i.moyenne) === f.key).length})
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Légende Needle */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <div style={{ width: "var(--legend-dot)", height: "var(--legend-dot)", borderRadius: "50%", border: "1.5px solid #94A3B8", background: "white" }} />
            <span style={{ fontSize: "var(--legend-fs)", color: "#94A3B8", fontWeight: 600 }}>Classe</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <div style={{ width: "var(--legend-dot)", height: "var(--legend-dot)", borderRadius: "50%", background: T.blueAccent }} />
            <span style={{ fontSize: "var(--legend-fs)", color: T.blueAccent, fontWeight: 600 }}>Élève</span>
          </div>
        </div>
      </div>

      {/* Grille de cards ou message vide */}
      {displayItems.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: T.textMuted }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>∅</div>
          <div style={{ fontSize: "var(--title-fs)", fontWeight: 500 }}>Aucun résultat</div>
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(var(--grid-min), 1fr))",
          gap: "var(--grid-gap)"
        }}>
          {displayItems.map((item, index) => (
            <AcquisitionCard
              key={item.titre + item.theme + index}
              item={item}
              viewLevel={viewLevel}
              index={index}
              loaded={loaded}
              niveauClasse={niveauClasse}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AcquisitionEleve;
